import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    useTheme,
    IconButton,
    Stack,
    Tooltip,
} from "@mui/material";
import { CalendarToday } from "@mui/icons-material";
import TodayIcon from "@mui/icons-material/Today";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
    format,
    eachDayOfInterval,
    startOfWeek,
    endOfWeek,
    addWeeks,
    addDays,
    addMonths,
    subMonths,
    isSameWeek,
    isSameMonth,
    getISOWeek,
    isToday,
    parse,
    startOfDay,
} from "date-fns";

const DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

function getToday() {
    return startOfDay(new Date());
}

function getCurrentWeekStart() {
    return startOfWeek(getToday(), { weekStartsOn: 1 });
}

function parseIsoWeek(isoWeekStr, fallback) {
    if (!isoWeekStr) return fallback;
    try {
        return startOfWeek(parse(isoWeekStr, "RRRR'W'II", new Date()), {
            weekStartsOn: 1,
        });
    } catch {
        return fallback;
    }
}

function getWeeksInMonth(month) {
    const weeks = [];
    let weekStart = startOfWeek(
        new Date(month.getFullYear(), month.getMonth(), 1),
        { weekStartsOn: 1 },
    );
    while (
        isSameMonth(weekStart, month) ||
        isSameMonth(endOfWeek(weekStart, { weekStartsOn: 1 }), month)
    ) {
        const days = eachDayOfInterval({
            start: weekStart,
            end: endOfWeek(weekStart, { weekStartsOn: 1 }),
        });
        weeks.push({ days, weekNumber: getISOWeek(weekStart) });
        weekStart = addWeeks(weekStart, 1);
    }
    return weeks;
}

export default function WeekPicker({
    isoWeek,
    getIsoWeek,
    stepWeeks = 1,
    buttonLabelFormatter,
    hideTodayButton = false,
}) {
    const theme = useTheme();
    const today = getToday();
    const currentWeekStart = getCurrentWeekStart();

    const [open, setOpen] = useState(false);
    const [selectedWeek, setSelectedWeek] = useState(() =>
        parseIsoWeek(isoWeek, currentWeekStart),
    );
    const [currentMonth, setCurrentMonth] = useState(() =>
        parseIsoWeek(isoWeek, currentWeekStart),
    );

    useEffect(() => {
        const parsed = parseIsoWeek(isoWeek, currentWeekStart);
        setSelectedWeek(parsed);
        setCurrentMonth(parsed);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isoWeek]);

    const weeksInMonth = getWeeksInMonth(currentMonth);

    const goToCurrentWeek = () => {
        const cw = getCurrentWeekStart();
        setSelectedWeek(cw);
        setCurrentMonth(cw);
        getIsoWeek(format(cw, "RRRR'W'II"));
    };

    const isSelectedWeek = (weekDayOne) =>
        selectedWeek &&
        isSameWeek(weekDayOne, selectedWeek, { weekStartsOn: 1 });

    const selectedPeriodStart = selectedWeek || currentWeekStart;
    const selectedPeriodEnd = addDays(
        endOfWeek(addWeeks(selectedPeriodStart, stepWeeks - 1), {
            weekStartsOn: 1,
        }),
        0,
    );

    const isDayInSelectedPeriod = (day) =>
        day >= selectedPeriodStart && day <= selectedPeriodEnd;

    const isCurrentWeek = (weekDayOne) =>
        isSameWeek(weekDayOne, today, { weekStartsOn: 1 });

    const handleOk = () => {
        if (selectedWeek) {
            getIsoWeek(format(selectedWeek, "RRRR'W'II"));
        }
        setOpen(false);
    };

    const goToPreviousWeek = () => {
        const newWeek = addWeeks(selectedWeek || currentWeekStart, -stepWeeks);
        setSelectedWeek(newWeek);
        setCurrentMonth(newWeek);
        getIsoWeek(format(newWeek, "RRRR'W'II"));
    };

    const goToNextWeek = () => {
        const newWeek = addWeeks(selectedWeek || currentWeekStart, stepWeeks);
        setSelectedWeek(newWeek);
        setCurrentMonth(newWeek);
        getIsoWeek(format(newWeek, "RRRR'W'II"));
    };

    const defaultButtonLabel = `Week ${getISOWeek(selectedWeek || currentWeekStart)}`;
    const buttonLabel = buttonLabelFormatter
        ? buttonLabelFormatter(selectedWeek || currentWeekStart)
        : defaultButtonLabel;

    return (
        <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <IconButton size="large" onClick={goToPreviousWeek}>
                    <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
                <Button
                    variant="outlined"
                    onClick={() => setOpen(true)}
                    endIcon={<CalendarToday fontSize="small" />}
                    sx={{ textTransform: "none" }}
                >
                    {buttonLabel}
                </Button>
                <IconButton size="large" onClick={goToNextWeek}>
                    <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
                {!hideTodayButton && !isSameWeek(selectedWeek || currentWeekStart, today, {
                    weekStartsOn: 1,
                }) && (
                    <Tooltip title="Go to current week">
                        <IconButton
                            size="small"
                            onClick={goToCurrentWeek}
                            color="primary"
                        >
                            <TodayIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        minWidth: 320,
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        pb: 1,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Typography variant="subtitle1" fontWeight={600}>
                            {selectedWeek
                                ? format(selectedWeek, "RRRR'W'II")
                                : format(currentWeekStart, "RRRR'W'II")}
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={goToCurrentWeek}
                            sx={{
                                textTransform: "none",
                                fontSize: "0.75rem",
                                py: 0.25,
                            }}
                        >
                            Current Week
                        </Button>
                    </Stack>
                </DialogTitle>

                <DialogContent sx={{ px: 2, pt: 2, pb: 0 }}>
                    {/* Month navigation */}
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={2}
                    >
                        <IconButton
                            size="small"
                            onClick={() =>
                                setCurrentMonth(subMonths(currentMonth, 1))
                            }
                        >
                            <ArrowBackIosNewIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="subtitle2" fontWeight={600}>
                            {format(currentMonth, "MMMM yyyy")}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() =>
                                setCurrentMonth(addMonths(currentMonth, 1))
                            }
                        >
                            <ArrowForwardIosIcon fontSize="small" />
                        </IconButton>
                    </Stack>

                    {/* Day-of-week header */}
                    <Box
                        display="grid"
                        gridTemplateColumns="32px repeat(7, 1fr)"
                        gap="2px"
                        mb={0.5}
                    >
                        <Box /> {/* spacer for week-number column */}
                        {DAY_LABELS.map((d, i) => (
                            <Typography
                                key={i}
                                align="center"
                                variant="caption"
                                sx={{
                                    color: "text.secondary",
                                    fontWeight: 600,
                                    lineHeight: "32px",
                                }}
                            >
                                {d}
                            </Typography>
                        ))}
                    </Box>

                    {/* Weeks */}
                    {weeksInMonth.map((week) => {
                        const weekSelected = week.days.some((day) =>
                            isDayInSelectedPeriod(day),
                        );
                        const weekIsCurrent = isCurrentWeek(week.days[0]);

                        return (
                            <Box
                                key={
                                    week.weekNumber +
                                    "-" +
                                    format(week.days[0], "yyyy-MM-dd")
                                }
                                display="grid"
                                gridTemplateColumns="32px repeat(7, 1fr)"
                                gap="2px"
                                mb="2px"
                                alignItems="center"
                                onClick={() => setSelectedWeek(week.days[0])}
                                sx={{
                                    cursor: "pointer",
                                    borderRadius: 1,
                                    "&:hover": {
                                        bgcolor: `${theme.palette.primary.main}14`,
                                    },
                                }}
                            >
                                {/* Week number badge */}
                                <Box
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: 1,
                                        bgcolor: weekIsCurrent
                                            ? theme.palette.primary.main
                                            : `${theme.palette.primary.main}20`,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        mx: "auto",
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: "0.7rem",
                                            color: weekIsCurrent
                                                ? "#fff"
                                                : theme.palette.primary.main,
                                        }}
                                    >
                                        {week.weekNumber}
                                    </Typography>
                                </Box>

                                {/* Days */}
                                {week.days.map((day) => {
                                    const dayIsToday = isToday(day);
                                    const dayInSelectedWeek =
                                        isDayInSelectedPeriod(day);

                                    return (
                                        <Box
                                            key={format(day, "yyyy-MM-dd")}
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: "50%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                position: "relative",
                                                bgcolor: dayInSelectedWeek
                                                    ? theme.palette.primary.main
                                                    : "transparent",
                                                // Override center day of selected week with today highlight
                                                ...(dayInSelectedWeek &&
                                                    dayIsToday && {
                                                        bgcolor:
                                                            theme.palette
                                                                .primary.dark,
                                                    }),
                                                ...(!dayInSelectedWeek &&
                                                    dayIsToday && {
                                                        border: `2px solid ${theme.palette.secondary.main}`,
                                                    }),
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: dayIsToday
                                                        ? 700
                                                        : 400,
                                                    color: dayInSelectedWeek
                                                        ? "#fff"
                                                        : dayIsToday
                                                          ? theme.palette
                                                                .secondary.main
                                                          : "text.primary",
                                                    lineHeight: 1,
                                                }}
                                            >
                                                {format(day, "d")}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        );
                    })}

                    {/* Legend */}
                    <Stack
                        direction="row"
                        spacing={2}
                        mt={2}
                        mb={1}
                        justifyContent="center"
                    >
                        <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                        >
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    border: `2px solid ${theme.palette.secondary.main}`,
                                }}
                            />
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Today
                            </Typography>
                        </Stack>
                        <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                        >
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: "50%",
                                    bgcolor: theme.palette.primary.main,
                                }}
                            />
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {stepWeeks > 1
                                    ? `Selected ${stepWeeks}-week period`
                                    : "Selected week"}
                            </Typography>
                        </Stack>
                    </Stack>
                </DialogContent>

                <DialogActions
                    sx={{
                        borderTop: `1px solid ${theme.palette.divider}`,
                        px: 2,
                    }}
                >
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleOk}
                        disableElevation
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
